import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { SpotAdminLogService, SpotAdminLog, ACTION_TYPES, ActionType } from "@/services/SpotAdminLogService";
import { ArrowPathIcon, FunnelIcon } from "@heroicons/react/24/outline";

const AdminLogPage: React.FC = () => {
  const { user } = useAuth();
  const [logs, setLogs] = useState<SpotAdminLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [undoingAction, setUndoingAction] = useState<number | null>(null);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [filterType, setFilterType] = useState<ActionType | "ALL">("ALL");
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const loadLogs = useCallback(async (reset: boolean = false) => {
    if (!user?.accesstoken) return;

    setLoading(true);
    const newOffset = reset ? 0 : offset;
    const actionTypeFilter = filterType === "ALL" ? undefined : filterType;

    const fetchedLogs = await SpotAdminLogService.getRecentLogs(
      user.accesstoken,
      limit,
      newOffset,
      actionTypeFilter
    );

    if (reset) {
      setLogs(fetchedLogs);
      setOffset(limit);
    } else {
      setLogs(prev => [...prev, ...fetchedLogs]);
      setOffset(prev => prev + limit);
    }

    setHasMore(fetchedLogs.length === limit);
    setLoading(false);
  }, [user?.accesstoken, filterType, limit, offset]);

  useEffect(() => {
    loadLogs(true);
  }, [user?.accesstoken, filterType]);

  const handleRefresh = () => {
    loadLogs(true);
  };

  const handleLoadMore = () => {
    loadLogs(false);
  };

  const handleFilterChange = (newFilter: ActionType | "ALL") => {
    setFilterType(newFilter);
    setOffset(0);
  };

  const handleUndoAction = async (logId: number, confirmWarnings: boolean = false): Promise<void> => {
    if (!user?.accesstoken) return;
    setUndoingAction(logId);
    const result = await SpotAdminLogService.undoAction(logId, user.accesstoken, confirmWarnings);
    if (result.success) {
      loadLogs(true);
      setMessage({ type: "success", text: result.message || "Action undone successfully" });
    } else if (result.warnings && result.warnings.length > 0) {
      const warningText = result.warnings.join("\n");
      if (window.confirm(`Warning:\n${warningText}\n\nDo you want to proceed?`)) {
        setUndoingAction(null);
        await handleUndoAction(logId, true);
        return;
      } else {
        setMessage({ type: "error", text: "Undo cancelled" });
      }
    } else {
      setMessage({ type: "error", text: result.message || "Failed to undo action" });
    }
    setUndoingAction(null);
    setTimeout(() => setMessage(null), 3000);
  };

  const actionTypes: { value: ActionType | "ALL"; label: string }[] = [
    { value: "ALL", label: "All Actions" },
    { value: ACTION_TYPES.MERGE, label: "Merge" },
    { value: ACTION_TYPES.RENAME, label: "Rename" },
    { value: ACTION_TYPES.EDIT, label: "Edit" },
    { value: ACTION_TYPES.CREATE, label: "Create" },
    { value: ACTION_TYPES.NOTE, label: "Note" },
    { value: ACTION_TYPES.UNDO_MERGE, label: "Undo Merge" },
    { value: ACTION_TYPES.UNDO_RENAME, label: "Undo Rename" },
    { value: ACTION_TYPES.UNDO_EDIT, label: "Undo Edit" },
  ];

  return (
    <div className="min-h-full bg-gray-900 py-6 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Admin Activity Log</h1>
          <p className="text-gray-400 text-sm mt-1">
            Track all admin actions on spots across the platform
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <FunnelIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <select
              value={filterType}
              onChange={(e) => handleFilterChange(e.target.value as ActionType | "ALL")}
              className="pl-9 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {actionTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowPathIcon className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div
          className={`mb-4 p-4 rounded-lg ${
            message.type === "success" ? "bg-green-900/50 text-green-300" : "bg-red-900/50 text-red-300"
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Stats Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-gray-800 rounded-lg p-4">
          <div className="text-gray-400 text-xs uppercase tracking-wide">Total Actions</div>
          <div className="text-2xl font-bold text-white mt-1">{logs.length}</div>
        </div>
        <div className="bg-gray-800 rounded-lg p-4">
          <div className="text-gray-400 text-xs uppercase tracking-wide">Merges</div>
          <div className="text-2xl font-bold text-purple-400 mt-1">
            {logs.filter(l => l.actionType === ACTION_TYPES.MERGE).length}
          </div>
        </div>
        <div className="bg-gray-800 rounded-lg p-4">
          <div className="text-gray-400 text-xs uppercase tracking-wide">Notes</div>
          <div className="text-2xl font-bold text-gray-400 mt-1">
            {logs.filter(l => l.actionType === ACTION_TYPES.NOTE).length}
          </div>
        </div>
        <div className="bg-gray-800 rounded-lg p-4">
          <div className="text-gray-400 text-xs uppercase tracking-wide">Undone</div>
          <div className="text-2xl font-bold text-amber-400 mt-1">
            {logs.filter(l => l.isUndone).length}
          </div>
        </div>
      </div>

      {/* Logs List */}
      <div className="bg-gray-800 rounded-lg overflow-hidden">
        {loading && logs.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-gray-600 border-t-blue-500 rounded-full animate-spin"></div>
            <span className="ml-3 text-gray-400">Loading activity log...</span>
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            No activity logged yet
          </div>
        ) : (
          <div className="divide-y divide-gray-700">
            {logs.map((log) => {
              const actionColor = SpotAdminLogService.getActionTypeColor(log.actionType);
              return (
                <div
                  key={log.id}
                  className={`p-4 hover:bg-gray-700/50 transition-colors ${
                    log.isUndone ? "opacity-60" : ""
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      {/* Header Row */}
                      <div className="flex items-center gap-3 mb-2">
                        <span
                          className="px-2 py-0.5 rounded text-xs font-semibold"
                          style={{ backgroundColor: actionColor.bg, color: actionColor.text }}
                        >
                          {SpotAdminLogService.formatActionType(log.actionType)}
                        </span>
                        {log.isUndone && (
                          <span className="text-amber-400 text-xs font-medium">UNDONE</span>
                        )}
                        <span className="text-gray-500 text-xs">
                          {SpotAdminLogService.formatRelativeTime(log.createdAt)}
                        </span>
                        <span className="text-gray-500 text-xs">
                          by {log.adminUsername || `Admin #${log.adminUserId}`}
                        </span>
                      </div>

                      {/* Content */}
                      <div className="text-gray-300 text-sm">
                        {log.actionType === ACTION_TYPES.MERGE && (
                          <>
                            <span className="text-gray-400">Merged </span>
                            <span className="font-medium text-white">{log.sourceSpotName}</span>
                            <span className="text-gray-500"> (ID: {log.sourceSpotId})</span>
                            <span className="text-gray-400"> into </span>
                            <span className="font-medium text-white">{log.targetSpotName}</span>
                            <span className="text-gray-500"> (ID: {log.targetSpotId})</span>
                            {(log.sessionsAffected || log.reviewsAffected) && (
                              <span className="text-gray-500 ml-2">
                                ({log.sessionsAffected} sessions, {log.reviewsAffected} reviews)
                              </span>
                            )}
                          </>
                        )}
                        {log.actionType === ACTION_TYPES.NOTE && (
                          <>
                            <span className="text-gray-400">Note on </span>
                            <span className="font-medium text-white">{log.spotName}</span>
                            <span className="text-gray-500"> (ID: {log.spotId})</span>
                            <span className="text-gray-400">: </span>
                            <span className="text-gray-300">{log.notes}</span>
                          </>
                        )}
                        {log.actionType === ACTION_TYPES.EDIT && (
                          <>
                            <span className="text-gray-400">Edited </span>
                            <span className="font-medium text-white">{log.spotName}</span>
                            <span className="text-gray-500"> (ID: {log.spotId})</span>
                          </>
                        )}
                        {log.actionType === ACTION_TYPES.RENAME && (
                          <>
                            <span className="text-gray-400">Renamed spot to </span>
                            <span className="font-medium text-white">{log.spotName}</span>
                            <span className="text-gray-500"> (ID: {log.spotId})</span>
                          </>
                        )}
                        {log.actionType === ACTION_TYPES.CREATE && (
                          <>
                            <span className="text-gray-400">Created </span>
                            <span className="font-medium text-white">{log.spotName}</span>
                            <span className="text-gray-500"> (ID: {log.spotId})</span>
                          </>
                        )}
                        {(log.actionType === ACTION_TYPES.UNDO_MERGE ||
                          log.actionType === ACTION_TYPES.UNDO_RENAME ||
                          log.actionType === ACTION_TYPES.UNDO_EDIT) && (
                          <>
                            <span className="text-gray-400">Reversed action on </span>
                            <span className="font-medium text-white">{log.spotName}</span>
                            <span className="text-gray-500"> (Log #{log.relatedLogId})</span>
                          </>
                        )}
                      </div>

                      {/* Undo Info */}
                      {log.isUndone && log.undoneAt && (
                        <div className="mt-2 text-xs text-gray-500">
                          Undone {SpotAdminLogService.formatRelativeTime(log.undoneAt)}
                          {log.undoneByUsername && ` by ${log.undoneByUsername}`}
                        </div>
                      )}

                      {/* Undo Warnings */}
                      {log.undoWarnings && log.undoWarnings.length > 0 && (
                        <div className="mt-2 p-2 bg-amber-900/30 rounded text-xs text-amber-300">
                          {log.undoWarnings.map((warning, idx) => (
                            <div key={idx}>Warning: {warning}</div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      {log.canUndo && !log.isUndone && (
                        <button
                          onClick={() => handleUndoAction(log.id)}
                          disabled={undoingAction === log.id}
                          className="px-3 py-1.5 border border-amber-500 text-amber-400 text-xs font-medium rounded hover:bg-amber-500/10 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {undoingAction === log.id ? "Undoing..." : "Undo"}
                        </button>
                      )}
                      {log.undoBlockedReason && !log.isUndone && (
                        <span className="text-xs text-red-400">{log.undoBlockedReason}</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Load More */}
        {hasMore && logs.length > 0 && (
          <div className="p-4 border-t border-gray-700">
            <button
              onClick={handleLoadMore}
              disabled={loading}
              className="w-full py-2 bg-gray-700 text-gray-300 text-sm rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Loading..." : "Load More"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminLogPage;
